import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import {
  addCrossClientClients,
  addCrossClientFetchedClientIds,
  selectCrossClientFetchedClientIds,
  selectCrossClientPageResults,
  setCrossClientClientsLoading,
} from '../../../../store/crossClient/crossClientResults'
import { useEffect, useMemo, useState } from 'react'
import { CrossClientQueryResult, EvaluatedCrossClientQueryResult } from '../../../../models/CrossClient'
import { selectAccountId } from '../../../../store/auth/authSlice'
import { useLazyGetAllUsersQuery } from '../../../../store/user/user'

export interface CrossClientQueryResultDetails {
  selectedResult: EvaluatedCrossClientQueryResult | null
  selectionSource: 'list' | 'detail' | null
  hasNextResult: boolean
  hasPreviousResult: boolean
  handleShowResultDetails: (result: CrossClientQueryResult) => void
  handleClearSelectedResult: () => void
  handleNextResult: () => void
  handlePreviousResult: () => void
}

const CLIENT_BATCH_TO_PRELOAD = 20

const ensureEvaluated = (result: CrossClientQueryResult): EvaluatedCrossClientQueryResult | null => (result.match ? { ...result, match: result.match } : null)

export const useCrossClientQueryResultDetails = (): CrossClientQueryResultDetails => {
  const dispatch = useAppDispatch()

  const accountId = useAppSelector(selectAccountId)
  const pageResults = useAppSelector(selectCrossClientPageResults)
  const fetchedClientIds = useAppSelector(selectCrossClientFetchedClientIds)

  const results = useMemo(
    () =>
      pageResults.flatMap((result) => {
        const evaluated = ensureEvaluated(result)
        return evaluated ? [evaluated] : []
      }),
    [pageResults],
  )

  const [triggerGetAllUsers] = useLazyGetAllUsersQuery()

  const [selectedResult, setSelectedResult] = useState<EvaluatedCrossClientQueryResult | null>(null)
  const [selectionSource, setSelectionSource] = useState<'list' | 'detail' | null>(null)

  const selectedResultIndex = useMemo(() => {
    if (!selectedResult) {
      return null
    }
    const index = results.findIndex((result) => result.id === selectedResult.id)
    return index !== -1 ? index : null
  }, [selectedResult, results])

  useEffect(() => {
    if (selectedResultIndex === null) {
      return
    }

    const nextResultIndex = selectedResultIndex + 1
    const nextResult = results[nextResultIndex]
    if (!nextResult || fetchedClientIds[nextResult.clientId]) {
      return
    }

    const nextBatch = results.slice(nextResultIndex, nextResultIndex + CLIENT_BATCH_TO_PRELOAD)
    const clientIds = nextBatch.map((result) => result.clientId).filter((clientId) => !fetchedClientIds[clientId])
    if (!clientIds.length) {
      return
    }

    const fetchMoreClients = async (clientIds: string[]) => {
      dispatch(setCrossClientClientsLoading(true))
      dispatch(addCrossClientFetchedClientIds(clientIds))
      try {
        const contacts = await triggerGetAllUsers({ accountId, ids: clientIds }).unwrap()
        if (contacts?.data) {
          const clients = contacts.data.filter((contact) => contact.roles.some((role) => role.name === 'Client'))
          dispatch(addCrossClientClients(clients))
        }
      } finally {
        dispatch(setCrossClientClientsLoading(false))
      }
    }

    void fetchMoreClients(clientIds)
  }, [dispatch, accountId, selectedResultIndex, results, fetchedClientIds, triggerGetAllUsers])

  const hasNextResult = selectedResultIndex !== null && selectedResultIndex < results.length - 1
  const hasPreviousResult = selectedResultIndex !== null && selectedResultIndex > 0

  const handleShowResultDetails = (result: CrossClientQueryResult) => {
    const evaluated = ensureEvaluated(result)
    if (!evaluated) {
      return
    }
    setSelectedResult(evaluated)
    setSelectionSource('list')
  }

  const handleClearSelectedResult = () => {
    setSelectedResult(null)
    setSelectionSource(null)
  }

  const handleNextResult = () => {
    if (hasNextResult) {
      setSelectedResult(results[selectedResultIndex + 1])
      setSelectionSource('detail')
    }
  }

  const handlePreviousResult = () => {
    if (hasPreviousResult) {
      setSelectedResult(results[selectedResultIndex - 1])
      setSelectionSource('detail')
    }
  }

  return {
    selectedResult,
    selectionSource,
    hasNextResult,
    hasPreviousResult,
    handleShowResultDetails,
    handleClearSelectedResult,
    handleNextResult,
    handlePreviousResult,
  }
}
